import { useState, type MouseEvent } from 'react';
import { IconButton } from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';
import { useDeleteFlowMutation } from '@/state/mutations/flows/use-delete-flow';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useNotifications } from '@/hooks/use-notifications';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useNavigate, useParams } from 'react-router';
import { FLOW_PARENT_ENTITY } from '@/types/dtos/flows/response/flow-parent-entity.enum';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { useEventStore } from '@/state/stores/use-event-store';

export const Delete = ({ flow }: { flow: FlowDto }) => {
  const t = useTranslations();
  const notification = useNotifications();
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId?: string }>();
  const event = useEventStore((s) => s.event);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { mutateAsync: deleteFlowMutation, isPending: isDeletingPending } = useDeleteFlowMutation();
  const mode = useEventMode();

  const handleDeleteIconClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteModalCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteFlow = async (mouseEvent: MouseEvent<HTMLButtonElement>) => {
    mouseEvent.stopPropagation();
    // Prevent multiple calls
    if (isDeletingPending) return;

    try {
      await deleteFlowMutation({ flowId: flow.id });
      // Close modal first before showing notification and navigating
      setIsDeleteDialogOpen(false);

      // Show success notification
      notification.notifySuccess(t('flows.detail.deleteModal.notifications.deleteSuccess', { title: flow.title }));

      // Navigate after a short delay to ensure notification is shown
      setTimeout(() => {
        const navigateToEntityBase = flow.parentType === FLOW_PARENT_ENTITY.EVENT ? '/event' : '/organization';
        // Get parent ID from event store, URL params, or flow.parent (in that order)
        const parentId =
          event?.id ||
          eventId ||
          (flow.parent
            ? typeof flow.parent === 'string'
              ? flow.parent
              : typeof flow.parent === 'object' && 'id' in flow.parent
                ? flow.parent.id
                : undefined
            : undefined);

        if (parentId) {
          navigate(`${navigateToEntityBase}/${parentId}?tab=flows`);
        } else {
          // If parent ID is not available, just navigate to the base path
          console.warn('Flow parent ID not available for navigation', { flow, event, eventId });
          navigate(navigateToEntityBase);
        }
      }, 100);
    } catch (error) {
      // Only show error notification if the mutation actually failed
      console.error('Flow deletion error:', error);
      notification.notifyError(t('flows.detail.deleteModal.notifications.deleteError'));
    }
  };

  return (
    <>
      <IconButton
        aria-label="delete"
        onClick={handleDeleteIconClick}
        loading={isDeletingPending}
        disabled={isOrganizeMode(mode)}
        size="small"
        sx={{
          width: '32px',
          height: '32px',
        }}
      >
        <DeleteOutlined fontSize="small" />
      </IconButton>
      <ConfirmationModal
        open={isDeleteDialogOpen}
        isLoading={isDeletingPending}
        title={t('flows.detail.deleteModal.title')}
        text={t('flows.detail.deleteModal.text', { title: flow.title })}
        onConfirm={handleDeleteFlow}
        onCancel={handleDeleteModalCancel}
      />
    </>
  );
};
